import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { NativeProps } from '@/src/components/interfaces';

export enum CPopoverPlacement {
  TopLeft = 'top-left',
  TopRight = 'top-right',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

export enum CPopoverBehaviour {
  Click = 'click',
  Hover = 'hover',
}

// --- Singleton context ---

interface PopoverRequest {
  content: React.ReactNode;
  placement: CPopoverPlacement;
  rect: DOMRect;
  id: string;
  behaviour: CPopoverBehaviour;
  maxWidth: number;
  maxHeight: number;
  scrollable: boolean;
}

interface PopoverContextValue {
  show(request: PopoverRequest): void;
  hide(id: string): void;
  isActive(id: string): boolean;
}

interface PopoverState {
  visible: boolean;
  content: React.ReactNode;
  top: number;
  left: number;
  id: string | null;
  behaviour: CPopoverBehaviour;
  placement: CPopoverPlacement;
  maxWidth: number;
  maxHeight: number;
  scrollable: boolean;
}

const HIDDEN: PopoverState = { visible: false, content: null, top: 0, left: 0, id: null, behaviour: CPopoverBehaviour.Click, placement: CPopoverPlacement.BottomLeft, maxWidth: 280, maxHeight: 400, scrollable: true };

const PopoverContext = createContext<PopoverContextValue | null>(null);

function usePopoverContext(): PopoverContextValue {
  const context = useContext(PopoverContext);
  if (!context) throw new Error('CPopover requires CPopoverProvider. Add CProvider to your app.');
  return context;
}

function computePopoverPosition(placement: CPopoverPlacement, rect: DOMRect): { top: number; left: number; resolved: CPopoverPlacement } {
  const threshold = 60;
  let resolved = placement;

  const fitsBottom = window.innerHeight - rect.bottom > threshold;
  const fitsTop = rect.top > threshold;

  if (resolved === CPopoverPlacement.BottomLeft && !fitsBottom) resolved = CPopoverPlacement.TopLeft;
  if (resolved === CPopoverPlacement.BottomRight && !fitsBottom) resolved = CPopoverPlacement.TopRight;
  if (resolved === CPopoverPlacement.TopLeft && !fitsTop) resolved = CPopoverPlacement.BottomLeft;
  if (resolved === CPopoverPlacement.TopRight && !fitsTop) resolved = CPopoverPlacement.BottomRight;

  const isTop = resolved === CPopoverPlacement.TopLeft || resolved === CPopoverPlacement.TopRight;
  const isRight = resolved === CPopoverPlacement.TopRight || resolved === CPopoverPlacement.BottomRight;

  const top = isTop ? rect.top : rect.bottom;
  const left = isRight ? rect.right : rect.left;

  return { top, left, resolved };
}

// --- Provider ---

export function CPopoverProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [state, setState] = useState<PopoverState>(HIDDEN);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activeIdRef = useRef<string | null>(null);

  const show = useCallback((request: PopoverRequest) => {
    const { top, left, resolved } = computePopoverPosition(request.placement, request.rect);
    clearTimeout(leaveTimer.current);
    activeIdRef.current = request.id;
    setState({ visible: true, content: request.content, top, left, id: request.id, behaviour: request.behaviour, placement: resolved, maxWidth: request.maxWidth, maxHeight: request.maxHeight, scrollable: request.scrollable });
  }, []);

  const hide = useCallback((id: string) => {
    leaveTimer.current = setTimeout(() => {
      setState(prev => {
        if (prev.id === id) {
          activeIdRef.current = null;
          return HIDDEN;
        }
        return prev;
      });
    }, 150);
  }, []);

  const isActive = useCallback((id: string) => activeIdRef.current === id, []);

  const cancelHide = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  const contextValue = useMemo(() => ({ show, hide, isActive }), [show, hide, isActive]);

  const handleClickOutside = useCallback(() => {
    clearTimeout(leaveTimer.current);
    setState(HIDDEN);
  }, []);

  const handleMouseLeavePopover = useCallback(() => {
    if (activeIdRef.current) hide(activeIdRef.current);
  }, [hide]);
  return (
    <PopoverContext.Provider value={contextValue}>
      {children}
      {state.visible && createPortal(
        <>
          {state.behaviour === 'click' && (
            <div className="c-popover__backdrop" onClick={handleClickOutside} />
          )}
          <div
            className={classNames('c-popover', `c-popover--${state.placement}`)}
            style={{ top: state.top, left: state.left, maxWidth: state.maxWidth, maxHeight: state.scrollable ? state.maxHeight : undefined, overflow: state.scrollable ? 'auto' : undefined }}
            onMouseEnter={state.behaviour === CPopoverBehaviour.Hover ? cancelHide : undefined}
            onMouseLeave={state.behaviour === CPopoverBehaviour.Hover ? handleMouseLeavePopover : undefined}
          >
            {state.content}
          </div>
        </>,
        document.body,
      )}
    </PopoverContext.Provider>
  );
}

// --- Trigger ---

export interface CPopoverProps extends NativeProps {
  content: React.ReactNode;
  placement?: CPopoverPlacement;
  behaviour?: CPopoverBehaviour;
  maxWidth?: number;
  maxHeight?: number;
  scrollable?: boolean;
}

export function CPopover(props: PropsWithChildren<CPopoverProps>): React.JSX.Element {
  const { show, hide, isActive } = usePopoverContext();
  const triggerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(Math.random().toString(36).slice(2));
  const placement = props.placement ?? CPopoverPlacement.BottomLeft;
  const behaviour = props.behaviour ?? CPopoverBehaviour.Click;

  const handleShow = useCallback(() => {
    if (!triggerRef.current) return;
    show({
      content: props.content,
      placement,
      rect: triggerRef.current.getBoundingClientRect(),
      id: idRef.current,
      behaviour,
      maxWidth: props.maxWidth ?? 280,
      maxHeight: props.maxHeight ?? 400,
      scrollable: props.scrollable ?? true,
    });
  }, [show, props.content, placement, behaviour, props.maxWidth]);

  const handleHide = useCallback(() => {
    hide(idRef.current);
  }, [hide]);

  const handleClick = useCallback(() => {
    if (isActive(idRef.current)) {
      handleHide();
    } else {
      handleShow();
    }
  }, [isActive, handleShow, handleHide]);

  return (
    <div
      ref={triggerRef}
      className={classNames('c-popover__trigger', props.class)}
      onClick={behaviour === CPopoverBehaviour.Click ? handleClick : undefined}
      onMouseEnter={behaviour === CPopoverBehaviour.Hover ? handleShow : undefined}
      onMouseLeave={behaviour === CPopoverBehaviour.Hover ? handleHide : undefined}
      id={props.id}
      data-test-selector={props.testSelector}
    >
      {props.children}
    </div>
  );
}
